import { CircleAlert, CircleCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type {
  PackedItemRedFlagDetail,
  PackedItemsOverviewPackagingItem,
} from '@/shared/hooks/api/packedItemsOverview'
import type { PackagingFieldCrossCheckIssueCode } from '@/shared/hooks/api/packagingSuppliers'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import { auditIssueLabelKey } from './packagingSupplierOverviewColumns'
import { deriveLatestPackagingItemAuditRunAt } from './packagingItemAuditFilter'
import { formatPackagingItemAuditRunTimestamp } from './formatPackagingItemAuditRunTimestamp'

type PackagedItemRedFlagsCellProps = {
  redFlags: PackagingFieldCrossCheckIssueCode[]
  redFlagDetails: PackedItemRedFlagDetail[]
  packagingItems?: PackedItemsOverviewPackagingItem[]
}

export function PackagedItemRedFlagsCell({
  redFlags,
  redFlagDetails,
  packagingItems = [],
}: PackagedItemRedFlagsCellProps) {
  const { t, i18n } = useTranslation('packagingSuppliers')
  const useChineseReasoning = i18n.language.startsWith('zh')

  if (redFlags.length === 0) {
    const lastRunAt = deriveLatestPackagingItemAuditRunAt(packagingItems)
    let hoverLabel = t('list.audit.notAudited')
    if (lastRunAt != null) {
      hoverLabel = formatPackagingItemAuditRunTimestamp(
        lastRunAt,
        i18n.language
      )
    }
    return (
      <HoverCard>
        <HoverCardTrigger asChild>
          <button
            type='button'
            className='inline-flex rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
            aria-label={hoverLabel}
            onClick={(event) => event.stopPropagation()}
          >
            <CircleCheck className='size-4 text-green-600 dark:text-green-500' aria-hidden />
          </button>
        </HoverCardTrigger>
        <HoverCardContent className='w-80 max-w-sm p-4' align='start' side='top'>
          <p className='text-sm text-popover-foreground'>{hoverLabel}</p>
        </HoverCardContent>
      </HoverCard>
    )
  }

  const summaryLabel = t('list.redFlags.hoverTitle')

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <button
          type='button'
          className='inline-flex rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
          aria-label={summaryLabel}
          onClick={(event) => event.stopPropagation()}
        >
          <CircleAlert className='size-4 text-destructive' aria-hidden />
        </button>
      </HoverCardTrigger>
      <HoverCardContent className='w-80 max-w-sm p-4' align='start' side='top'>
        <ul className='space-y-3'>
          {redFlagDetails.map((detail) => (
            <li
              key={`${detail.packagingItemId}-${detail.code}`}
              className='flex items-start gap-2'
            >
              <CircleAlert
                className='mt-0.5 size-4 shrink-0 text-destructive'
                aria-hidden
              />
              <div className='min-w-0 space-y-0.5'>
                <p className='text-sm font-medium text-popover-foreground'>
                  {detail.packagingItemName}
                </p>
                <p className='text-sm font-medium text-popover-foreground'>
                  {t(auditIssueLabelKey(detail.code))}
                </p>
                <p className='text-sm text-muted-foreground'>
                  {useChineseReasoning
                    ? detail.reasoningZh
                    : detail.reasoningEn}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </HoverCardContent>
    </HoverCard>
  )
}
